export const RING_BUFFER_STORAGE_KEY = 'aha-presenter-performance-log'
export const SAMPLE_EVENT_NAME = 'Presenter Performance Sample'
export const FREEZE_EVENT_NAME = 'Presenter Freeze'

export const SAMPLE_INTERVAL_MS = 5000
export const LAG_TICK_MS = 500
export const MIXPANEL_SUMMARY_INTERVAL_MS = 60000
export const MIXPANEL_AUDIENCE_THRESHOLD = 200
export const FREEZE_THRESHOLD_MS = 1000
export const FREEZE_EPISODE_MS = 2000
export const FREEZE_MIXPANEL_MIN_GAP_MS = 30000
export const FREEZE_MIXPANEL_MAX_PER_SESSION = 5
export const MAX_BUFFER_ENTRIES = 200
export const MAX_BUFFER_CHARS = 150000

const MEGABYTE = 1024 * 1024

const roundTo = (value, digits = 1) => {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

const emptyWindow = () => ({
  longTaskCount: 0,
  longTaskTotalMs: 0,
  longTaskMaxMs: 0,
  freezeCount: 0,
  frameGapMaxMs: 0,
  lagMaxMs: 0,
  lagTotalMs: 0,
  lagTicks: 0,
  storageWriteCount: 0,
  storageWriteChars: 0,
  storageWriteMs: 0,
  storageWriteMaxChars: 0,
  mqttMessages: 0,
  socketMessages: 0,
})

export const readDeviceInfo = (navigatorRef) => ({
  deviceMemoryGb: navigatorRef?.deviceMemory ?? null,
  cpuCores: navigatorRef?.hardwareConcurrency ?? null,
})

export const readMemory = (performanceRef) => {
  const memory = performanceRef?.memory
  if (!memory || !memory.jsHeapSizeLimit) return { heapUsedMb: null, heapLimitMb: null }
  return {
    heapUsedMb: roundTo(memory.usedJSHeapSize / MEGABYTE),
    heapLimitMb: roundTo(memory.jsHeapSizeLimit / MEGABYTE, 0),
  }
}

export const trimBuffer = (entries, maxEntries = MAX_BUFFER_ENTRIES, maxChars = MAX_BUFFER_CHARS) => {
  let trimmed = entries.length > maxEntries ? entries.slice(entries.length - maxEntries) : entries.slice()
  let serialised = JSON.stringify(trimmed)
  while (serialised.length > maxChars && trimmed.length > 1) {
    trimmed = trimmed.slice(Math.ceil(trimmed.length / 10))
    serialised = JSON.stringify(trimmed)
  }
  return { entries: trimmed, serialised }
}

export const readStoredBuffer = (storage) => {
  try {
    const parsed = JSON.parse(storage.getItem(RING_BUFFER_STORAGE_KEY))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const createRingBuffer = (storage, writeItem) => {
  let entries = readStoredBuffer(storage).slice(-MAX_BUFFER_ENTRIES)
  const persist = () => {
    try {
      const { entries: kept, serialised } = trimBuffer(entries)
      entries = kept
      writeItem(RING_BUFFER_STORAGE_KEY, serialised)
    } catch {
      entries = entries.slice(Math.ceil(entries.length / 2))
    }
  }
  return {
    record(entry) {
      entries.push(entry)
      persist()
    },
  }
}

const createFreezeReporter = ({
  now, wallClock, readContext, readMemoryNow, getStats, buffer, track, visibility,
}) => {
  let lastFreezeAt = -Infinity
  let lastMixpanelAt = -Infinity
  let mixpanelSent = 0
  return function reportFreeze(trigger, durationMs) {
    const t = now()
    if (t - lastFreezeAt < FREEZE_EPISODE_MS) return
    lastFreezeAt = t
    const stats = getStats()
    stats.freezeCount += 1
    const context = readContext()
    const properties = {
      trigger,
      blockedMs: Math.round(durationMs),
      ...context,
      visibility: visibility(),
      ...readMemoryNow(),
      storageWriteCountSinceLastSample: stats.storageWriteCount,
      storageWriteCharsSinceLastSample: stats.storageWriteChars,
      storageWriteMsSinceLastSample: Math.round(stats.storageWriteMs),
    }
    buffer.record({ kind: 'freeze', at: wallClock(), ...properties })

    const isLargeAudience = (context.audienceCount || 0) >= MIXPANEL_AUDIENCE_THRESHOLD
    const isAllowedByRateLimit = t - lastMixpanelAt >= FREEZE_MIXPANEL_MIN_GAP_MS &&
      mixpanelSent < FREEZE_MIXPANEL_MAX_PER_SESSION
    if (isLargeAudience && isAllowedByRateLimit) {
      lastMixpanelAt = t
      mixpanelSent += 1
      track(FREEZE_EVENT_NAME, properties)
    }
  }
}

const watchLongTasks = ({ PerformanceObserverClass, getStats, reportFreeze }) => {
  if (!PerformanceObserverClass) return () => {}
  let recent = []
  const onEntries = (list) => {
    list.getEntries().forEach((entry) => {
      const stats = getStats()
      const { duration } = entry
      stats.longTaskCount += 1
      stats.longTaskTotalMs += duration
      stats.longTaskMaxMs = Math.max(stats.longTaskMaxMs, duration)
      const endedAt = entry.startTime + duration
      recent = recent.filter((task) => task.endedAt > endedAt - 1000)
      recent.push({ endedAt, duration })
      const blockedInSecond = recent.reduce((sum, task) => sum + task.duration, 0)
      if (duration > FREEZE_THRESHOLD_MS) {
        reportFreeze('single_task', duration)
        recent = []
      } else if (blockedInSecond > FREEZE_THRESHOLD_MS) {
        reportFreeze('rolling_second', blockedInSecond)
        recent = []
      }
    })
  }
  try {
    const observer = new PerformanceObserverClass(onEntries)
    observer.observe({ type: 'longtask', buffered: false })
    return () => observer.disconnect()
  } catch {
    return () => {}
  }
}

const watchEventLoopLag = ({
  win, doc, now, getStats,
}) => {
  let lastTick = now()
  const resetClock = () => { lastTick = now() }
  const timer = win.setInterval(() => {
    const t = now()
    const lag = Math.max(0, t - lastTick - LAG_TICK_MS)
    lastTick = t
    if (doc.hidden) return
    const stats = getStats()
    stats.lagTicks += 1
    stats.lagTotalMs += lag
    stats.lagMaxMs = Math.max(stats.lagMaxMs, lag)
  }, LAG_TICK_MS)
  doc.addEventListener?.('visibilitychange', resetClock)
  return () => {
    win.clearInterval(timer)
    doc.removeEventListener?.('visibilitychange', resetClock)
  }
}

const watchFrameGaps = ({
  win, doc, now, getStats, reportFreeze,
}) => {
  if (!win.requestAnimationFrame) return () => {}
  let lastFrameAt = now()
  let handle = null
  const onFrame = () => {
    const t = now()
    const gap = t - lastFrameAt
    lastFrameAt = t
    if (!doc.hidden) {
      const stats = getStats()
      stats.frameGapMaxMs = Math.max(stats.frameGapMaxMs, gap)
      if (gap > FREEZE_THRESHOLD_MS) reportFreeze('frame_stall', gap)
    }
    handle = win.requestAnimationFrame(onFrame)
  }
  const resetClock = () => { lastFrameAt = now() }
  handle = win.requestAnimationFrame(onFrame)
  doc.addEventListener?.('visibilitychange', resetClock)
  return () => {
    win.cancelAnimationFrame(handle)
    doc.removeEventListener?.('visibilitychange', resetClock)
  }
}

const watchStorageWrites = ({ StorageClass, now, getStats }) => {
  const storagePrototype = StorageClass.prototype
  const originalSetItem = storagePrototype.setItem
  const patchedSetItem = function patchedSetItem(key, value) {
    if (key === RING_BUFFER_STORAGE_KEY) return originalSetItem.call(this, key, value)
    const startedAt = now()
    try {
      return originalSetItem.call(this, key, value)
    } finally {
      const stats = getStats()
      const chars = String(key).length + String(value).length
      stats.storageWriteCount += 1
      stats.storageWriteChars += chars
      stats.storageWriteMaxChars = Math.max(stats.storageWriteMaxChars, chars)
      stats.storageWriteMs += now() - startedAt
    }
  }
  storagePrototype.setItem = patchedSetItem
  return {
    writeItem: (key, value, storage) => originalSetItem.call(storage, key, value),
    restore: () => {
      if (storagePrototype.setItem === patchedSetItem) storagePrototype.setItem = originalSetItem
    },
  }
}

const watchInbound = (inboundCounters, getStats) => {
  const fieldByName = { mqtt: 'mqttMessages', socket: 'socketMessages' }
  const unsubscribers = Object.entries(inboundCounters).map(([name, subscribe]) => {
    try {
      return subscribe(() => {
        const stats = getStats()
        stats[fieldByName[name]] += 1
      }) || (() => {})
    } catch {
      return () => {}
    }
  })
  return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
}

const summariseStats = (stats, seconds) => ({
  longTaskCount: stats.longTaskCount,
  longTaskTotalMs: Math.round(stats.longTaskTotalMs),
  longTaskMaxMs: Math.round(stats.longTaskMaxMs),
  freezeCount: stats.freezeCount,
  frameGapMaxMs: Math.round(stats.frameGapMaxMs),
  lagMaxMs: Math.round(stats.lagMaxMs),
  lagAvgMs: stats.lagTicks ? roundTo(stats.lagTotalMs / stats.lagTicks) : 0,
  storageWriteCount: stats.storageWriteCount,
  storageWriteCharsPerSec: Math.round(stats.storageWriteChars / seconds),
  storageWriteMaxChars: stats.storageWriteMaxChars,
  storageWriteMs: Math.round(stats.storageWriteMs),
  mqttMessagesPerSec: roundTo(stats.mqttMessages / seconds),
  socketMessagesPerSec: roundTo(stats.socketMessages / seconds),
})

const mergeStats = (target, source) => Object.keys(source).reduce((merged, key) => ({
  ...merged,
  [key]: key.includes('Max') ? Math.max(target[key], source[key]) : target[key] + source[key],
}), {})

/**
 * Starts presenter performance telemetry and returns a `stop` function.
 * Everything it patches or schedules is undone by `stop`.
 *
 * @param {object} deps
 * @param {() => object} deps.getContext presentation/slide/audience snapshot, read on every sample
 * @param {(eventName: string, props: object) => void} deps.track analytics sink
 * @param {{mqtt?: Function, socket?: Function}} [deps.inboundCounters] each takes a message callback and returns an unsubscribe
 */
export function startPresenterPerformanceTelemetry({
  getContext, track: trackUnsafe, inboundCounters = {}, env = {},
}) {
  const track = (eventName, properties) => {
    try {
      trackUnsafe(eventName, properties)
    } catch {
      // analytics failure must never reach the presenter
    }
  }
  const win = env.window || window
  const doc = env.document || document
  const perf = env.performance || performance
  const storage = env.storage || win.localStorage
  const StorageClass = env.StorageClass || win.Storage
  const navigatorRef = env.navigator || win.navigator
  const now = () => perf.now()
  const wallClock = () => (env.dateNow ? env.dateNow() : Date.now())
  const readContext = () => {
    try {
      return getContext() || {}
    } catch {
      return {}
    }
  }

  let windowStats = emptyWindow()
  let summaryStats = emptyWindow()
  const getStats = () => windowStats

  const storageWrites = watchStorageWrites({ StorageClass, now, getStats })
  const buffer = createRingBuffer(storage, (key, value) => storageWrites.writeItem(key, value, storage))
  const reportFreeze = createFreezeReporter({
    now,
    wallClock,
    readContext,
    readMemoryNow: () => readMemory(perf),
    getStats,
    buffer,
    track,
    visibility: () => doc.visibilityState,
  })
  const stopCallbacks = [
    storageWrites.restore,
    watchLongTasks({
      PerformanceObserverClass: env.PerformanceObserver || win.PerformanceObserver,
      getStats,
      reportFreeze,
    }),
    watchEventLoopLag({
      win, doc, now, getStats,
    }),
    watchFrameGaps({
      win, doc, now, getStats, reportFreeze,
    }),
    watchInbound(inboundCounters, getStats),
  ]

  let lastSampleAt = now()
  let lastSummaryAt = now()
  const takeSample = () => {
    const t = now()
    const elapsedMs = t - lastSampleAt
    lastSampleAt = t
    buffer.record({
      kind: 'sample',
      at: wallClock(),
      windowMs: Math.round(elapsedMs),
      ...readContext(),
      visibility: doc.visibilityState,
      ...readMemory(perf),
      ...summariseStats(windowStats, Math.max(elapsedMs / 1000, 0.001)),
    })
    summaryStats = mergeStats(summaryStats, windowStats)
    windowStats = emptyWindow()

    if (t - lastSummaryAt < MIXPANEL_SUMMARY_INTERVAL_MS) return
    const seconds = (t - lastSummaryAt) / 1000
    lastSummaryAt = t
    const context = readContext()
    if ((context.audienceCount || 0) >= MIXPANEL_AUDIENCE_THRESHOLD) {
      track(SAMPLE_EVENT_NAME, {
        ...context,
        visibility: doc.visibilityState,
        ...readDeviceInfo(navigatorRef),
        ...readMemory(perf),
        windowSeconds: Math.round(seconds),
        ...summariseStats(summaryStats, seconds),
      })
    }
    summaryStats = emptyWindow()
  }
  const sampleTimer = win.setInterval(takeSample, SAMPLE_INTERVAL_MS)

  buffer.record({
    kind: 'start', at: wallClock(), ...readContext(), ...readDeviceInfo(navigatorRef),
  })

  let stopped = false
  return function stop() {
    if (stopped) return
    stopped = true
    win.clearInterval(sampleTimer)
    stopCallbacks.forEach((callback) => callback())
    buffer.record({ kind: 'stop', at: wallClock() })
  }
}
